import type { IReservationTableRef } from '@/interfaces/IReservation';
import type { ISector } from '@/interfaces/ISector';
import type { ITable } from '@/interfaces/ITable';

/** A table as the combination picker shows it: reservation data plus placement. */
export interface IReservationGroupTableOption extends IReservationTableRef {
  shape: ITable['shape'];
  posX: number | null;
  posY: number | null;
  w: number;
  h: number;
}

export interface IReservationGroupArea {
  sector: ISector;
  tables: IReservationGroupTableOption[];
}

/**
 * Merges the settings' table list with the floor-plan placement of each
 * table. The layout is fetched when the picker opens, so its sector wins
 * over the one in the (older) settings bundle.
 */
export function groupTableOptions(
  refs: readonly IReservationTableRef[],
  layout: readonly ITable[]
): IReservationGroupTableOption[] {
  const byId = new Map(layout.map((table) => [table.id, table]));
  return refs.map((ref) => {
    const placed = byId.get(ref.id);
    return {
      ...ref,
      sectorId: placed ? placed.sectorId : ref.sectorId,
      shape: placed?.shape ?? 'square',
      posX: placed?.posX ?? null,
      posY: placed?.posY ?? null,
      w: placed?.w ?? 1,
      h: placed?.h ?? 1,
    };
  });
}

function isPlaced(table: IReservationGroupTableOption, sectorIds: Set<number>) {
  return (
    table.sectorId !== null &&
    sectorIds.has(table.sectorId) &&
    table.posX !== null &&
    table.posY !== null
  );
}

/** Sectors of the branch that have at least one table drawn on their plan, in order. */
export function groupAreas(
  sectors: readonly ISector[],
  branchId: number,
  tables: readonly IReservationGroupTableOption[]
): IReservationGroupArea[] {
  const ordered = sectors
    .filter((sector) => sector.branchId === branchId)
    .sort((a, b) => a.index - b.index);
  const ids = new Set(ordered.map((sector) => sector.id));
  return ordered
    .map((sector) => ({
      sector,
      tables: tables.filter(
        (table) => table.sectorId === sector.id && isPlaced(table, ids)
      ),
    }))
    .filter((area) => area.tables.length > 0);
}

/** Tables the plan cannot show: no sector, a deleted sector, or no position. */
export function unplacedGroupTables(
  areas: readonly IReservationGroupArea[],
  tables: readonly IReservationGroupTableOption[]
): IReservationGroupTableOption[] {
  const shown = new Set(
    areas.flatMap((area) => area.tables.map((table) => table.id))
  );
  return tables.filter((table) => !shown.has(table.id));
}
